import { useAuth } from "@/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { CategoryBadges } from "@/components/CategoryBadges";
import { CategorySectorComparison } from "@/components/CategorySectorComparison";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { getPeriodOptions } from "@/lib/periodOptions";
import { Calendar, Loader2, Plus, Lock, Unlock, Zap, Car, Trash2, Droplets, Package } from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";

/**
 * Calculator Page - Gamified Workflow
 * 
 * Layout:
 * 1. Header: Period selector + New Period button
 * 2. Dashboard "Your Impact": Badges + Progress + Sector Comparison
 * 3. Section "Add Activities by Category": Visual cards per category
 * 4. Workflow: Click category → Show FE list → Inline form
 */
export default function CalculatorNew() {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFactorKey, setSelectedFactorKey] = useState<string>("");
  const [activityValue, setActivityValue] = useState("");
  const [description, setDescription] = useState("");

  // Period creation state
  const [isCreatePeriodOpen, setIsCreatePeriodOpen] = useState(false);
  const [periodType, setPeriodType] = useState<"month" | "quarter" | "year">("month");
  const [periodLabel, setPeriodLabel] = useState("");
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());

  // Fetch periods
  const { data: periodsData, isLoading: loadingPeriods, refetch: refetchPeriods } = trpc.periods.list.useQuery();
  const periods = periodsData || [];

  // Fetch emission factors
  const { data: factorsData, isLoading: loadingFactors } = trpc.calculator.getFactors.useQuery();
  const factors = factorsData || [];

  // Fetch diagnostic data
  const { data: diagnosticData } = trpc.calculator.diagnostic.useQuery();

  // Fetch emissions for selected period
  const { data: emissions, isLoading: loadingEmissions, refetch: refetchEmissions } = 
    trpc.calculator.listByPeriod.useQuery(
      { periodId: parseInt(selectedPeriod) },
      { enabled: !!selectedPeriod && !isNaN(parseInt(selectedPeriod)) }
    );

  // Fetch sector comparison data
  const { data: sectorComparison } = trpc.calculator.getSectorComparison.useQuery(
    { periodId: selectedPeriod },
    { enabled: !!selectedPeriod && !!emissions && emissions.length > 0 }
  );

  // Group factors by category
  const categorizedFactors = useMemo(() => {
    if (!factors.length) return {};
    
    return factors.reduce((acc, factor) => {
      const category = factor.category || "Other";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(factor);
      return acc;
    }, {} as Record<string, typeof factors>);
  }, [factors]);

  // Category icons mapping
  const categoryIcons: Record<string, React.ReactNode> = {
    Energy: <Zap className="h-6 w-6" />,
    Transport: <Car className="h-6 w-6" />,
    Waste: <Trash2 className="h-6 w-6" />,
    Water: <Droplets className="h-6 w-6" />,
    Other: <Package className="h-6 w-6" />,
  };

  // Category colors
  const categoryColors: Record<string, string> = {
    Energy: "bg-yellow-500",
    Transport: "bg-blue-500",
    Waste: "bg-green-500",
    Water: "bg-cyan-500",
    Other: "bg-gray-500",
  };

  // Selected period data
  const selectedPeriodData = periods.find((p) => p.id === selectedPeriod);

  // Selected factor
  const selectedFactor = factors.find((f) => f.fe_key === selectedFactorKey);

  // Estimated emissions
  const estimatedEmissions = useMemo(() => {
    if (!selectedFactor || !activityValue) return 0;
    return parseFloat(activityValue) * selectedFactor.fe_value;
  }, [selectedFactor, activityValue]);

  // Auto-select first period
  useEffect(() => {
    if (periods.length > 0 && !selectedPeriod) {
      setSelectedPeriod(periods[0].id);
    }
  }, [periods, selectedPeriod]);

  // Mutations
  const createMutation = trpc.calculator.createEntry.useMutation({
    onSuccess: () => {
      toast.success("Emission added successfully");
      refetchEmissions();
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to add emission: ${error.message}`);
    },
  });

  const createPeriodMutation = trpc.periods.create.useMutation({
    onSuccess: () => {
      toast.success("Period created successfully");
      refetchPeriods();
      setIsCreatePeriodOpen(false);
      setPeriodLabel("");
    },
    onError: (error) => {
      toast.error(`Failed to create period: ${error.message}`);
    },
  });

  const closePeriodMutation = trpc.periods.close.useMutation({
    onSuccess: () => {
      toast.success("Period closed successfully");
      refetchPeriods();
    },
    onError: (error) => {
      toast.error(`Failed to close period: ${error.message}`);
    },
  });

  const reopenPeriodMutation = trpc.periods.update.useMutation({
    onSuccess: () => {
      toast.success("Period reopened successfully");
      refetchPeriods();
    },
    onError: (error) => {
      toast.error(`Failed to reopen period: ${error.message}`);
    },
  });

  const resetForm = () => {
    setSelectedCategory(null);
    setSelectedFactorKey("");
    setActivityValue("");
    setDescription("");
  };

  const handleSubmit = () => {
    if (!selectedPeriod || !selectedFactorKey || !activityValue) {
      toast.error("Please fill all required fields");
      return;
    }

    createMutation.mutate({
      periodId: parseInt(selectedPeriod),
      feKey: selectedFactorKey,
      activityValue: parseFloat(activityValue),
      unit: selectedFactor?.unit || "",
      tco2e: estimatedEmissions,
      notes: description || undefined,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header: Period Selector */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Carbon Calculator</CardTitle>
                <CardDescription>Track and manage your emissions by period</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="w-full sm:w-[300px]">
                    <SelectValue placeholder="Select a period" />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map((period) => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.period_label}{" "}
                        <span className="text-xs text-muted-foreground">
                          ({period.status === 'closed' ? "closed" : period.status})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => setIsCreatePeriodOpen(true)}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Period
                </Button>
              </div>
              
              {/* Admin controls */}
              {selectedPeriod && user?.role && ['mypa_super_admin', 'mypa_data_admin'].includes(user.role) && (
                <div className="flex gap-2 items-center p-3 bg-muted/50 rounded-md">
                  <span className="text-sm text-muted-foreground">Admin controls:</span>
                  {selectedPeriodData?.status === 'closed' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (confirm(`Reopen period "${selectedPeriodData.period_label}"?`)) {
                          reopenPeriodMutation.mutate({
                            periodId: selectedPeriod,
                            status: 'open',
                          });
                        }
                      }}
                      disabled={reopenPeriodMutation.isPending}
                    >
                      {reopenPeriodMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Unlock className="mr-2 h-4 w-4" />
                      )}
                      Reopen Period
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (confirm(`Close period "${selectedPeriodData?.period_label}"? This will prevent any further emissions from being added.`)) {
                          closePeriodMutation.mutate({ periodId: selectedPeriod });
                        }
                      }}
                      disabled={closePeriodMutation.isPending}
                    >
                      {closePeriodMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Lock className="mr-2 h-4 w-4" />
                      )}
                      Close Period
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dashboard: Your Impact */}
        {selectedPeriod && emissions && emissions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Badges</CardTitle>
              </CardHeader>
              <CardContent>
                <CategoryBadges emissions={emissions} factors={factors} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Total Emissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {emissions.reduce((sum, e) => sum + e.tco2e, 0).toFixed(2)} kgCO₂e
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {emissions.length} activities recorded
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sector Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                {sectorComparison && sectorComparison.length > 0 ? (
                  <CategorySectorComparison data={sectorComparison} />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Add more emissions to see sector comparison
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Section: Add Activities by Category */}
        {selectedPeriod && selectedPeriodData?.status !== 'closed' && (
          <Card>
            <CardHeader>
              <CardTitle>Add Activities by Category</CardTitle>
              <CardDescription>
                Select a category to add emissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingFactors ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading categories...
                </div>
              ) : Object.keys(categorizedFactors).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {Object.entries(categorizedFactors).map(([category, categoryFactors]) => (
                    <Card
                      key={category}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedCategory === category ? "ring-2 ring-primary" : ""
                      }`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      <CardContent className="p-4 text-center space-y-2">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${categoryColors[category] || "bg-gray-500"} text-white`}>
                          {categoryIcons[category] || <Package className="h-6 w-6" />}
                        </div>
                        <h3 className="font-semibold">{category}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {categoryFactors.length} factors
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="p-6 bg-muted/50 rounded-md space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      No emission factors available for your sector.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Please contact your administrator to configure the mapping table.
                    </p>
                  </div>
                  
                  {/* Diagnostic information */}
                  {diagnosticData && (
                    <div className="border-t pt-4 space-y-3">
                      <h4 className="font-semibold text-sm">Diagnostic Information</h4>
                      
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Profile company_id:</span>
                          <span className="font-mono font-medium">{diagnosticData.companyId || 'NULL'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Company name:</span>
                          <span className="font-medium">{diagnosticData.companyName || 'Not found'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Company activity:</span>
                          <span className="font-mono font-medium">{diagnosticData.companyActivity || 'Not set'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Profile job_code:</span>
                          <span className="font-mono font-medium">{diagnosticData.profileData?.job_code || 'Not set'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Used job_code:</span>
                          <span className="font-mono font-medium">{diagnosticData.userJobCode || 'Not set'}</span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total mapping entries:</span>
                          <span className="font-medium">{diagnosticData.totalMappingEntries}</span>
                        </div>
                      </div>
                      
                      {diagnosticData.mappingJobCodes && diagnosticData.mappingJobCodes.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">Available job_codes in mapping table:</p>
                          <div className="max-h-[200px] overflow-y-auto space-y-1">
                            {diagnosticData.mappingJobCodes.map((item) => (
                              <div
                                key={item.jobCode}
                                className={`flex justify-between p-2 rounded text-xs ${
                                  item.isMatch
                                    ? 'bg-green-100 dark:bg-green-900/20 border border-green-300'
                                    : 'bg-background border'
                                }`}
                              >
                                <span className="font-mono">{item.jobCode}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {item.feCount} FE
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {diagnosticData.error && (
                        <div className="p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive">
                          Error: {diagnosticData.error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Inline form when category selected */}
              {selectedCategory && categorizedFactors[selectedCategory] && (
                <div className="mt-6 p-4 border rounded-md space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{selectedCategory} - Select Factor</h3>
                    <Button variant="ghost" size="sm" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Emission Factor *</Label>
                    <Select value={selectedFactorKey} onValueChange={setSelectedFactorKey}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an emission factor" />
                      </SelectTrigger>
                      <SelectContent>
                        {categorizedFactors[selectedCategory].map((factor) => (
                          <SelectItem key={factor.fe_key} value={factor.fe_key}>
                            {factor.process_name} ({factor.unit})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedFactor && (
                      <p className="text-xs text-muted-foreground">
                        FE: {selectedFactor.fe_value} kgCO₂e/{selectedFactor.unit}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Activity Value *</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={activityValue}
                        onChange={(e) => setActivityValue(e.target.value)}
                        placeholder="Enter value"
                        className="flex-1"
                      />
                      <div className="flex items-center px-3 bg-muted rounded-md min-w-[60px] justify-center">
                        <span className="text-sm text-muted-foreground">
                          {selectedFactor?.unit || "-"}
                        </span>
                      </div>
                    </div>
                    {estimatedEmissions > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Estimated emissions:{" "}
                        <span className="font-semibold text-foreground">
                          {estimatedEmissions.toFixed(2)} kgCO₂e
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional description"
                    />
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={!selectedFactorKey || !activityValue || createMutation.isPending}
                    className="w-full"
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Emission
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Period closed message */}
        {selectedPeriod && selectedPeriodData?.status === 'closed' && (
          <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
            <CardContent className="p-4">
              <p className="text-sm text-orange-800 dark:text-orange-200">
                This period is closed. You cannot add emissions to a closed period.
                Please create a new period or ask an administrator to reopen this one.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Create Period Dialog */}
        <Dialog open={isCreatePeriodOpen} onOpenChange={setIsCreatePeriodOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Period</DialogTitle>
              <DialogDescription>
                Create a new reporting period for your emissions
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select
                  value={periodType}
                  onValueChange={(value) => setPeriodType(value as "month" | "quarter" | "year")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="quarter">Quarterly</SelectItem>
                    <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Period Label</Label>
                <Select value={periodLabel} onValueChange={setPeriodLabel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {getPeriodOptions(periodType, periodYear).map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={periodYear}
                  onChange={(e) => setPeriodYear(parseInt(e.target.value))}
                  min={2000}
                  max={2100}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreatePeriodOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!periodLabel) {
                    toast.error("Please select a period label");
                    return;
                  }
                  createPeriodMutation.mutate({
                    periodType,
                    periodLabel,
                    year: periodYear,
                  });
                }}
                disabled={!periodLabel || createPeriodMutation.isPending}
              >
                {createPeriodMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Period"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

